import React from 'react';
import { Card, Typography, Tooltip, Spin } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import redStopLamp from 'assets/icons/redStopLamp.svg';
import protectLamp from 'assets/icons/protectLamp.svg';
import milLamp from 'assets/icons/milLamp.svg';
import amberWarningLamp from 'assets/icons/amberWarningLamp.svg';

const { Text } = Typography;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: calc(100% - 40px);
`;

const StyledCard = styled(Card)`
  height: 100%;

  .ant-card-body {
    padding: 24px;
    height: 100%;
  }

  .widget-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;

    .ant-typography {
      margin: 0;
      color: #1f3eb8;
      font-weight: 600;
    }

    .info-icon {
      color: #8c8c8c;
      cursor: pointer;
    }
  }

  .lights-container {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    height: calc(100% - 40px);
  }

  .light-item {
    display: flex;
    align-items: center;
    gap: 16px;
    cursor: pointer;
    padding: 8px;
    border-radius: 8px;
    transition:
      background-color 0.2s,
      transform 0.1s;

    &:hover {
      background-color: #f5f5f5;
      transform: translateY(-2px);

      .light-label {
        text-decoration: underline;
        text-decoration-color: #9b9b9b;
        text-decoration-thickness: 1px;
        text-underline-offset: 3px;
      }
    }

    .light-icon {
      width: 36px;
      height: 36px;
      flex-shrink: 0;
    }

    .light-content {
      flex: 1;

      .light-count {
        font-size: 1.5rem;
        font-weight: bold;
        line-height: 1;
        margin-bottom: 2px;
      }

      .light-label {
        font-size: 0.75rem;
        line-height: 1.2;
      }
    }

    &.stop-lamp {
      .light-count,
      .light-label {
        color: #ff0000;
      }
    }

    &.protect-lamp,
    &.check-engine {
      .light-count,
      .light-label {
        color: #ff812a;
      }
    }

    &.mil-lamp {
      .light-count,
      .light-label {
        color: #ff812a;
      }
    }

    &.check-engine {
      .light-count,
      .light-label {
        color: #ff812a;
      }
    }
  }
`;

const AssetsWithEngineLightsWidget = ({
  isLoading = false,
  assetsEngineLightsData = {
    stopLamp: 0,
    protectLamp: 0,
    milLamp: 0,
    checkEngine: 0,
  },
  onEngineLightClick,
}) => {
  const tooltipContent = (
    <div>
      <div>• Displays how many assets have at least one engine light on.</div>
      <br />
      <div>
        • E.g., if an asset has a Stop light on and a Mil light on, it will
        count once under "Stop Lamp" and once under "Mil Lamp."
      </div>
    </div>
  );

  const handleLightClick = (lightType) => {
    if (onEngineLightClick) {
      onEngineLightClick(lightType);
    }
  };

  if (isLoading) {
    return (
      <StyledCard>
        <div className="widget-header">
          <Text strong>Assets with Engine Lights</Text>
          <Tooltip title={tooltipContent} overlayStyle={{ maxWidth: '420px' }}>
            <ExclamationCircleOutlined className="info-icon" />
          </Tooltip>
        </div>
        <LoadingContainer>
          <Spin size="large" />
        </LoadingContainer>
      </StyledCard>
    );
  }

  return (
    <StyledCard>
      <div className="widget-header">
        <Text strong>Assets with Engine Lights</Text>
        <Tooltip title={tooltipContent} overlayStyle={{ maxWidth: '420px' }}>
          <ExclamationCircleOutlined className="info-icon" />
        </Tooltip>
      </div>

      <div className="lights-container">
        <div
          className="light-item check-engine"
          onClick={() => handleLightClick('checkEngine')}
        >
          <img
            src={amberWarningLamp}
            alt="Check Engine"
            className="light-icon"
          />
          <div className="light-content">
            <div className="light-count">
              {assetsEngineLightsData.checkEngine}
            </div>
            <div className="light-label">Check Engine</div>
          </div>
        </div>

        <div
          className="light-item mil-lamp"
          onClick={() => handleLightClick('milLamp')}
        >
          <img src={milLamp} alt="Mil Lamp" className="light-icon" />
          <div className="light-content">
            <div className="light-count">{assetsEngineLightsData.milLamp}</div>
            <div className="light-label">Mil Lamp</div>
          </div>
        </div>

        <div
          className="light-item protect-lamp"
          onClick={() => handleLightClick('protectLamp')}
        >
          <img src={protectLamp} alt="Protect Lamp" className="light-icon" />
          <div className="light-content">
            <div className="light-count">
              {assetsEngineLightsData.protectLamp}
            </div>
            <div className="light-label">Protect Lamp</div>
          </div>
        </div>

        <div
          className="light-item stop-lamp"
          onClick={() => handleLightClick('stopLamp')}
        >
          <img src={redStopLamp} alt="Stop Lamp" className="light-icon" />
          <div className="light-content">
            <div className="light-count">{assetsEngineLightsData.stopLamp}</div>
            <div className="light-label">Stop Lamp</div>
          </div>
        </div>
      </div>
    </StyledCard>
  );
};

export default AssetsWithEngineLightsWidget;
